import React, { useState, useEffect, useRef } from "react";
import styles from "./AllMessaging.module.css";
import EmojiPicker from "emoji-picker-react";
import { FaPaperPlane, FaSmile, FaImage } from "react-icons/fa";

// 📦 Utility to clean "3 count" or similar
const cleanMembers = (value) => {
  const num = parseInt(value, 10);
  return isNaN(num) ? 0 : num;
};

const AllMessaging = ({
  type = "direct",
  data = [],
  searchQuery = "",
  filterUnread = false,
}) => {
  const [selectedContact, setSelectedContact] = useState(null);
  const [messageInput, setMessageInput] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [chatData, setChatData] = useState([]);
  const emojiRef = useRef();
  const messagesEndRef = useRef();

  // Update chatData when data changes
  useEffect(() => {
    let filtered = [...data];

    if (searchQuery) {
      filtered = filtered.filter((item) =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (filterUnread) {
      filtered = filtered.filter((item) => item.unread > 0);
    }

    setChatData(filtered);
    setSelectedContact(null); // Reset selection on tab change
  }, [data, searchQuery, filterUnread]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [selectedContact, chatData]);

  // Emoji outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiRef.current && !emojiRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };
    if (showEmojiPicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmojiPicker]);

  const handleSend = () => {
    if (!messageInput.trim() || selectedContact === null) return;

    const newMessage = {
      from: "You",
      avatar: "https://i.pravatar.cc/150?img=33",
      text: messageInput.trim(),
      time: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      side: "right",
      read: true,
    };

    const updated = [...chatData];
    updated[selectedContact].chat = updated[selectedContact].chat || [];
    updated[selectedContact].chat.push(newMessage);
    updated[selectedContact].unread = 0;

    setChatData(updated);
    setMessageInput("");
    setShowEmojiPicker(false);
  };

  const handleSelectContact = (index) => {
    const updated = [...chatData];
    const chat = updated[index].chat || [];

    updated[index].chat = chat.map((msg) =>
      msg.side === "left" ? { ...msg, read: true } : msg
    );
    updated[index].unread = 0;

    setChatData(updated);
    setSelectedContact(index);
  };

  return (
    <div className={styles.container}>
      {/* LEFT SIDE: CHAT LIST */}
      <div className={styles.sidebar}>
        {chatData.map((contact, index) => {
          const lastMsg = contact.chat?.[contact.chat.length - 1];
          return (
            <div
              key={index}
              className={`${styles.chatCard} ${
                selectedContact === index ? styles.active : ""
              }`}
              onClick={() => handleSelectContact(index)}
            >
              <div className={styles.avatarWrapper}>
                <img src={contact.avatar} alt={contact.name} />
              </div>
              <div className={styles.chatText}>
                <span className={styles.userName}>{contact.name}</span>
                <p className={contact.unread > 0 ? styles.unreadMsg : ""}>
                  {lastMsg?.text || "No messages yet"}
                </p>
              </div>
              <div className={styles.meta}>
                <span>{lastMsg?.time || ""}</span>
                {contact.unread > 0 && (
                  <div className={styles.badge}>{contact.unread}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* RIGHT SIDE: CHAT MESSAGES */}
      <div className={styles.rightSideContainer}>
        {selectedContact !== null ? (
          <>
            {/* HEADER */}
            <div className={styles.header}>
              <div className={styles.headerLeft}>
                <img
                  src={chatData[selectedContact].avatar}
                  alt="user"
                  className={styles.headerAvatar}
                />
                <span className={styles.userName}>
                  {chatData[selectedContact].title}
                </span>
              </div>

              {/* Only for group */}
              {type === "group" && (
                <span className={styles.memberCount}>
                  {cleanMembers(chatData[selectedContact].members)}{" "}
                  {cleanMembers(chatData[selectedContact].members) === 1
                    ? "Member"
                    : "Members"}
                </span>
              )}
            </div>

            {/* CHAT MESSAGES */}
            <div className={styles.messages}>
              {(chatData[selectedContact].chat || []).map((msg, i) => (
                <div
                  key={i}
                  className={`${styles.message} ${
                    msg.side === "right" ? styles.right : styles.left
                  }`}
                >
                  <img src={msg.avatar} alt={msg.from} />
                  <div
                    className={`${styles.bubble} ${
                      msg.side === "left" && msg.read === false
                        ? styles.unreadBubble
                        : ""
                    }`}
                  >
                    {msg.text}
                  </div>
                  <span className={styles.time}>{msg.time}</span>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* EMOJI PICKER */}
            {showEmojiPicker && (
              <div
                ref={emojiRef}
                style={{
                  position: "absolute",
                  bottom: "120px",
                  left: "60px",
                  zIndex: 1000,
                }}
              >
                <EmojiPicker
                  onEmojiClick={(e) => setMessageInput((p) => p + e.emoji)}
                />
              </div>
            )}

            {/* MESSAGE INPUT */}
            <div className={styles.bottomBorder}>
              <div className={styles.sendingmessege}>
                <input
                  type="text"
                  placeholder="Type Message Here ..."
                  className={styles.inputBox}
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSend()}
                />
              </div>
              <div className={styles.inputArea}>
                <div className={styles.inputLeft}>
                  <button
                    onClick={() => document.getElementById("fileInput").click()}
                  >
                    <FaImage />
                  </button>
                  <input
                    type="file"
                    id="fileInput"
                    style={{ display: "none" }}
                    accept="image/*"
                  />
                  <button onClick={() => setShowEmojiPicker(!showEmojiPicker)}>
                    <FaSmile />
                  </button>
                </div>
                <div className={styles.inputRight}>
                  <span>Translate</span>
                  <span>AI suggestion</span>
                  <button onClick={handleSend}>
                    <FaPaperPlane />
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <p className={styles.selectPrompt}>
            Select any contact to show chats
          </p>
        )}
      </div>
    </div>
  );
};

export default AllMessaging;
